import React from 'react';
import { FilterDropdown } from './FilterDropdown/FilterDropdown';
import { st, classes } from './Filters.st.css';
import { useDailyAgendaActions } from '../../../components/DailyAgenda/hooks/useDailyAgendaActions';
import { FilterOption, FilterType } from '../../../types/types';
import { FiltersViewModel } from '../../../components/DailyAgenda/viewModel/filtersViewModel/filtersViewModel';
import {
  getSelectedFilters,
  getSelectedOptionsFromList,
  getUpdatedFilters,
  hasSelectedFilters,
} from './utils';
import { TextButton } from 'wix-ui-tpa/cssVars';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { Clear } from '@wix/wix-ui-icons-common/on-stage';
import { FiltersDataHooks } from './consts';
import { WidgetComponents, WidgetElements } from '../../../utils/bi/consts';

export type FiltersProps = {
  filtersViewModel?: FiltersViewModel;
};

export const Filters: React.FC<FiltersProps> = ({ filtersViewModel }) => {
  const { onFiltersChange, onElementClicked } = useDailyAgendaActions();
  const { filters, filterByText } = filtersViewModel || {};
  const { t } = useTranslation();

  if (!filters) {
    return null;
  }

  const onFilterOptionClick = (
    type: FilterType,
    filterOption: FilterOption,
  ) => {
    const updatedFilters = getUpdatedFilters(filters, type, filterOption);
    onFiltersChange(getSelectedFilters(updatedFilters));
    onElementClicked(WidgetComponents.FILTER, WidgetElements.FILTER_BUTTON, {
      filterType: type,
      filterOption: filterOption.value,
    });
  };

  const onClearFilters = () => {
    onFiltersChange([]);
    onElementClicked(WidgetComponents.FILTER, WidgetElements.CLEAR_BUTTON);
  };

  const getDropdownLabel = (label: string, options: FilterOption[]) => {
    const count = getSelectedOptionsFromList(options).length;
    return count
      ? t('daily-agenda.filters.selected-option', {
          label,
          count,
        })
      : label;
  };

  return (
    <div className={classes.root} data-hook={FiltersDataHooks.ROOT}>
      {filters?.map(({ type, label, options }) => (
        <div
          key={type}
          className={st(classes.filterDropdownContainer, { filterType: type })}
        >
          <FilterDropdown
            label={getDropdownLabel(label, options)}
            options={options}
            dataHook={type + '-filter'}
            onChange={(filterOption) => onFilterOptionClick(type, filterOption)}
            filterByText={filterByText!}
          />
        </div>
      ))}
      {hasSelectedFilters(filters) && (
        <TextButton
          className={classes.clearAllButton}
          suffixIcon={<Clear />}
          onClick={onClearFilters}
          data-hook={FiltersDataHooks.CLEAR_FILTERS}
        >
          {t('daily-agenda.filters.clear-all')}
        </TextButton>
      )}
    </div>
  );
};
